// Hook "pull to refresh" (tirar para refrescar) para listas, al estilo nativo.
//
// Detecta un gesto de arrastre hacia abajo cuando la página está en el tope
// (scrollY === 0) y, superado un umbral, ejecuta `onRefresh`. Devuelve la
// distancia de arrastre actual y si está refrescando, para que la vista pinte
// un indicador. Degrada en silencio en escritorio (no hay touch) y respeta el
// scroll normal (solo actúa en el borde superior).
//
// Es una MEJORA PROGRESIVA: si no hay soporte táctil, no hace nada.

import { useCallback, useEffect, useRef, useState } from 'react';
import { haptic } from './haptics.js';

interface Options {
  /** Distancia (px) que hay que arrastrar para disparar el refresco. */
  threshold?: number;
  /** Arrastre máximo visible (se amortigua por encima del umbral). */
  max?: number;
  /** Deshabilita el gesto (p. ej. mientras ya se está cargando). */
  disabled?: boolean;
}

export function usePullToRefresh(onRefresh: () => void | Promise<void>, options: Options = {}) {
  const { threshold = 72, max = 110, disabled = false } = options;
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const active = useRef(false);

  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  const reset = useCallback(() => {
    startY.current = null;
    active.current = false;
    setPull(0);
  }, []);

  useEffect(() => {
    if (disabled) return;

    const onTouchStart = (e: TouchEvent) => {
      // Solo iniciamos el gesto si estamos en el tope del documento.
      if (window.scrollY > 0) return;
      startY.current = e.touches[0]?.clientY ?? null;
      active.current = true;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!active.current || startY.current === null) return;
      const y = e.touches[0]?.clientY ?? 0;
      const delta = y - startY.current;
      if (delta <= 0) {
        // Arrastre hacia arriba: no es pull-to-refresh.
        setPull(0);
        return;
      }
      // Amortiguación: cuanto más se arrastra, menos avanza (sensación elástica).
      const damped = Math.min(max, delta * 0.5);
      setPull(damped);
    };

    const onTouchEnd = () => {
      if (!active.current) return;
      if (pull >= threshold && !refreshing) {
        setRefreshing(true);
        haptic('light');
        void Promise.resolve(onRefreshRef.current()).finally(() => {
          setRefreshing(false);
          reset();
        });
      } else {
        reset();
      }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [disabled, pull, threshold, max, refreshing, reset]);

  return { pull, refreshing, threshold };
}
